import axios from 'axios';
import { logoutUser, updateToken } from '../slice/UserSlice';
import { saveReturnUrl } from './return-url-helper';
import { attemptTokenRefresh } from './tokenRefresh';
import { getApiUrl } from './config';

let store = null;
let isRefreshing = false;
let failedQueue = [];

/**
 * Initialize the interceptor with the Redux store.
 * Must be called after store creation in index.js.
 */
export function setStore(reduxStore) {
    store = reduxStore;
}

/**
 * Process queued requests after token refresh completes.
 * @param {Error|null} error - Error if refresh failed, null if successful
 * @param {string|null} token - New token if refresh succeeded
 */
function processQueue(error, token = null) {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
}

/**
 * Force logout and redirect to login page.
 * Saves current URL for return after re-login.
 */
function forceLogout(error) {
    console.warn('Session expired. Redirecting to login.');
    saveReturnUrl(window.location.pathname + window.location.search);
    if (store) {
        store.dispatch(logoutUser());
    }
    return Promise.reject(error);
}

/**
 * Handle 401 Unauthorized responses by attempting token refresh first.
 * If refresh fails, logs out the user.
 * Uses mutex to prevent multiple concurrent refresh attempts.
 * @param {Error} error - The axios error object
 * @returns {Promise} - Resolves with retried response or rejects with error
 */
export async function handle401Error(error) {
    const originalRequest = error.config;

    // Prevent infinite loops - if retry already attempted, force logout
    if (originalRequest._retry) {
        return forceLogout(error);
    }

    if (!store) {
        return Promise.reject(error);
    }

    const state = store.getState();
    const refreshToken = state.authUser?.refreshToken;

    // No refresh token available - force logout
    if (!refreshToken) {
        return forceLogout(error);
    }

    // If already refreshing, queue this request to retry after refresh completes
    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
        }).then(token => {
            originalRequest.headers['Authorization'] = token;
            return axios({
                ...originalRequest,
                baseURL: getApiUrl()
            });
        }).catch(err => {
            return Promise.reject(err);
        });
    }

    // Mark that we're attempting refresh and this request has been retried
    originalRequest._retry = true;
    isRefreshing = true;

    try {
        const refreshedAuth = await attemptTokenRefresh(refreshToken);
        const newToken = refreshedAuth?.token;
        const newRefreshToken = refreshedAuth?.refreshToken;

        // Update Redux state with new token
        store.dispatch(updateToken({ token: newToken, refreshToken: newRefreshToken }));

        // Process any queued requests with the new token
        processQueue(null, newToken);

        // Retry the original request with new token
        originalRequest.headers['Authorization'] = newToken;
        return axios({
            ...originalRequest,
            baseURL: getApiUrl()
        });
    } catch (refreshError) {
        // Refresh failed - notify queued requests and force logout
        processQueue(refreshError, null);
        return forceLogout(error);
    } finally {
        isRefreshing = false;
    }
}
